import React, {useState} from "react";
import {Badge, Collapse, Table} from "react-bootstrap";
import {PiCaretDown, PiCaretRight} from "react-icons/pi";
import HelpTooltip from "../../HelpTooltip.jsx";
import {formatQuantity} from "../../Statistics/format.js";
import {formatFarbnummer, produktlinieLabel} from "../format.js";

function formatDatumKurz(iso) {
    if (!iso) {
        return "–";
    }
    const datum = new Date(iso);
    if (Number.isNaN(datum.getTime())) {
        return String(iso);
    }
    return datum.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"});
}

/**
 * Je Containerkunde eine aufklappbare Zeile mit seinen Farbtönen im
 * gewählten Zeitraum (Meeting 22.09.2026 Punkt 4: "wer wann wie viel
 * bestellt hat"). Kunden ohne Bestellung im Zeitraum verschwinden
 * vollständig statt eine leere Zeile zu zeigen (Hausvorgabe). Kein
 * natives <select>/<details> - eigene Aufklapp-Logik mit
 * react-bootstrap Collapse, wie im übrigen Haus üblich.
 */
export default function ContainerkundenTabelle({kunden}) {
    const [offenId, setOffenId] = useState(null);

    const sichtbareKunden = (kunden || []).filter((k) => k.summe_menge > 0);
    if (sichtbareKunden.length === 0) {
        return null;
    }

    return (
        <Table hover responsive className="align-middle mb-4">
            <thead>
                <tr className="small text-muted text-uppercase">
                    <th style={{width: 32}}></th>
                    <th>Kunde</th>
                    <th>Kundennummer</th>
                    <th className="text-end">
                        Menge
                        <HelpTooltip placement="top" size={0.7} className="ms-1">
                            Summe über alle Farbtöne dieses Kunden im gewählten Zeitraum.
                        </HelpTooltip>
                    </th>
                    <th className="text-end">Belege</th>
                    <th>
                        Für Saisonalität
                        <HelpTooltip placement="top" size={0.7} className="ms-1">
                            „Linear verteilt" heißt: Seine Bestellungen zählen bei den Saisonfaktoren nicht in ihrem
                            tatsächlichen Bestellmonat, sondern gleichmäßig über das Jahr verteilt. „Nicht aktiv"
                            heißt: der Kunde ist gepflegt, aber die Sonderbehandlung ist derzeit ausgeschaltet - seine
                            Bestellungen zählen wie bei jedem anderen Kunden.
                        </HelpTooltip>
                    </th>
                </tr>
            </thead>
            <tbody>
                {sichtbareKunden.map((kunde) => {
                    const offen = offenId === kunde.id;

                    return (
                        <React.Fragment key={kunde.id}>
                            <tr
                                role="button"
                                aria-expanded={offen}
                                onClick={() => setOffenId(offen ? null : kunde.id)}
                                style={{cursor: "pointer"}}
                            >
                                <td className="text-muted">{offen ? <PiCaretDown aria-hidden="true" /> : <PiCaretRight aria-hidden="true" />}</td>
                                <td className="fw-semibold">{kunde.kundenname}</td>
                                <td className="text-muted small">
                                    {kunde.kundennummer_weclapp}
                                    {kunde.kundennummer_alterp ? ` (Alt-ERP ${kunde.kundennummer_alterp})` : ""}
                                </td>
                                <td className="text-end">{formatQuantity(kunde.summe_menge)} Stk.</td>
                                <td className="text-end">{formatQuantity(kunde.summe_belege)}</td>
                                <td>
                                    {kunde.aktiv ? (
                                        <Badge bg="info-subtle" text="info-emphasis">linear verteilt</Badge>
                                    ) : (
                                        <Badge bg="secondary-subtle" text="secondary-emphasis">nicht aktiv</Badge>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan={6} className="p-0 border-0">
                                    <Collapse in={offen}>
                                        <div>
                                            <div className="bg-body-secondary rounded p-3 mb-2 mx-2">
                                                <Table size="sm" borderless className="mb-0">
                                                    <thead>
                                                        <tr className="small text-muted text-uppercase">
                                                            <th>Farbton</th>
                                                            <th>Produktlinie</th>
                                                            <th className="text-end">Menge</th>
                                                            <th className="text-end">Belege</th>
                                                            <th>Erste Bestellung</th>
                                                            <th>Letzte Bestellung</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {kunde.farbtoene.map((f) => (
                                                            <tr key={f.artikel_nummer}>
                                                                <td>
                                                                    {f.farbnummer ? (
                                                                        <>
                                                                            {formatFarbnummer(f.farbnummer, f.farbnummer_unter)}
                                                                            {f.farbname ? ` ${f.farbname}` : ""}
                                                                        </>
                                                                    ) : (
                                                                        f.artikel_nummer
                                                                    )}
                                                                    <span className="text-muted small ms-1">({f.artikel_nummer})</span>
                                                                </td>
                                                                <td className="small text-muted">{produktlinieLabel(f.produktlinie)}</td>
                                                                <td className="text-end">{formatQuantity(f.menge)} Stk.</td>
                                                                <td className="text-end">{formatQuantity(f.belege)}</td>
                                                                <td className="small">{formatDatumKurz(f.erste_bestellung)}</td>
                                                                <td className="small">{formatDatumKurz(f.letzte_bestellung)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    </Collapse>
                                </td>
                            </tr>
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>
    );
}
